import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";import { CalendarDays, Users, MapPin, Sparkles, ArrowRight, ArrowLeft, Linkedin } from "lucide-react";
import TopNav from "../components/TopNav";
import Orbs from "../components/Orbs";
import { Section } from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtTimePT, fmtDatePT, fmtWeekdayPT, TZ } from "../lib/time";
import RichText from "../components/RichText";
import VerifyGate from "../components/VerifyGate";
import { needsVerification } from "../lib/register";
import { resolveImageUrl } from "../lib/media";
import { sessionTitle } from "../lib/sessionTitle";

const MEMBER_NOTE = "You must be a member to attend. Please use the email associated with your Venture Dock membership.";

export default function CapitalSeriesPage() {
  const { id } = useParams();
  const { user, member } = useAuth();
  const [series, setSeries] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get(`/sessions/series/${id}`).then((r) => setSeries(r.data));
    api.get(`/sessions/series/${id}/slots`).then((r) => setSlots(r.data));
  }, [id]);

  const reload = () => api.get(`/sessions/series/${id}/slots`).then((r) => setSlots(r.data));

  const totalOpen = useMemo(() => slots.filter((s) => s.status === "available" && !s.is_past).length, [slots]);

  const byWeek = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      const day = fmtDatePT(s.start_at, { year: "numeric", month: "2-digit", day: "2-digit" });
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(s);
    });
    return [...map.entries()].map(([k, v]) => ({ key: k, slots: v.sort((a, b) => a.start_at.localeCompare(b.start_at)) }));
  }, [slots]);

  if (!series) return <div className="relative min-h-screen"><Orbs /><div className="relative z-10"><TopNav /><Section className="pt-16"><div className="skeleton h-64" /></Section></div></div>;

  return (
    <div className="relative min-h-screen" data-testid="capital-series-page">
      <Orbs />
      <div className="relative z-10">
        <TopNav variant={user?.role === "admin" ? "admin" : "public"} />
        <Section className="pt-6 pb-4">
          <div className="mx-auto w-full max-w-[1160px]">
            <Link to="/events" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="back-to-events"><ArrowLeft size={14} /> All events</Link>
          </div>
        </Section>

        {/*
          Page header. The title used to be absent at page level and instead
          repeated on the investor card AND on every single day card, so a
          four-week series printed the same sentence five times. It belongs
          here, once, where it can anchor the page.
        */}
        <Section className="pb-5">
          <div className="mx-auto w-full max-w-[1160px]">
            <div className="chip chip-purple text-[10px]">{series.program || "Founder Labs"}</div>
            <h1 className="font-heading text-3xl lg:text-[34px] leading-tight mt-2" data-testid="series-title">{sessionTitle(series)}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2.5 text-xs text-muted-foreground">
              <Detail icon={Users} label={`${series.investor_name}${series.investor_firm ? ` · ${series.investor_firm}` : ""}`} />
              <Detail icon={MapPin} label={series.campus_id === "cmp_pa" ? "Palo Alto" : "Milpitas"} />
              <Detail icon={Sparkles} label={series.booking_mode === "approval" ? "Approval-based" : "First-come, first-served"} />
              {totalOpen > 0 && <Detail icon={CalendarDays} label={`${totalOpen} slot${totalOpen === 1 ? "" : "s"} open`} />}
            </div>
          </div>
        </Section>

        {/*
          Desktop layout: narrative left, booking rail right.

          The previous grid was [340px_1fr] — a narrow column for the long
          investor bio and an unbounded one for a handful of time buttons. On a
          1500px screen that gave the slot grid ~1100px, so `grid-cols-4`
          stretched each tile to ~250px of mostly empty space while the bio was
          squeezed into 340px, and everything below the last card was dead air.
          Inverting it puts the long-form content in a readable measure and
          fixes the rail at a width the tiles actually suit; capping the pair at
          1160px stops the whole thing sprawling on ultrawide.
        */}
        <Section className="pb-24">
          <div className="mx-auto w-full max-w-[1160px] grid lg:grid-cols-[minmax(0,1fr)_400px] gap-6 items-start">
            <div className="glass-panel p-6 lg:p-7 order-2 lg:order-1" data-testid="active-investor-card">
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-2xl overflow-hidden border border-white/60 bg-neutral-100 shrink-0">
                  <img src={resolveImageUrl(series.investor_photo)} alt={series.investor_name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="min-w-0">
                  <div className="font-heading text-lg leading-tight">{series.investor_name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{series.investor_firm}</div>
                  {series.investor_linkedin && (
                    <a href={series.investor_linkedin.startsWith("http") ? series.investor_linkedin : `https://${series.investor_linkedin}`} target="_blank" rel="noreferrer" className="mt-1.5 text-xs font-mono hover:underline inline-flex items-center gap-1.5" style={{ color: "hsl(var(--vd-green))" }} data-testid="investor-linkedin-link">
                      <Linkedin size={12} strokeWidth={2.25} />
                      <span className="lowercase">linkedin</span>
                      <span aria-hidden="true">↗</span>
                    </a>
                  )}
                </div>
              </div>
              <RichText html={series.investor_bio} className="text-sm text-muted-foreground mt-5 leading-relaxed" testId="series-investor-bio" />
              {series.focus_areas?.length > 0 && (
                <div className="mt-5 pt-4 border-t border-white/50 flex flex-wrap gap-1.5">
                  {series.focus_areas.map((f) => <span key={f} className="chip text-[10px]">{f}</span>)}
                </div>
              )}
            </div>

            {/* Booking rail — sticky on desktop so the times stay reachable
                however long the bio runs; first in the source order on mobile,
                where the action matters more than the backstory. */}
            <div className="space-y-4 order-1 lg:order-2 lg:sticky lg:top-24 lg:self-start" data-testid="booking-rail">
              {byWeek.length === 0 && (
                <div className="glass-panel p-6 text-sm text-muted-foreground" data-testid="no-slots">
                  No times are open for this session yet. Check back shortly.
                </div>
              )}
              {byWeek.map((wk) => {
                const openCount = wk.slots.filter((s) => s.status === "available" && !s.is_past).length;
                const first = wk.slots[0];
                const activeSelected = selected && wk.slots.some((s) => s.id === selected.id);
                return (
                  <div key={wk.key} className="glass-panel p-5" data-testid={`week-${wk.key}`}>
                    <div className="flex items-baseline justify-between gap-3 mb-4">
                      <div>
                        <div className="overline">{fmtWeekdayPT(first.start_at)}</div>
                        <div className="font-heading text-xl mt-0.5" data-testid={`week-title-${wk.key}`}>{new Date(first.start_at).toLocaleDateString("en-US", { timeZone: TZ, month: "long", day: "numeric" })}</div>
                      </div>
                      <div className="font-mono text-xs text-muted-foreground shrink-0">
                        <span style={{ color: openCount > 0 ? "hsl(var(--vd-green))" : "hsl(var(--muted-foreground))" }}>{openCount}</span> / {wk.slots.length} open
                      </div>
                    </div>
                    {/* Two up in a fixed-width rail keeps a tile near its natural
                        size instead of stretching one time across a quarter of
                        the viewport. */}
                    <div className="grid grid-cols-2 gap-2.5">
                      {wk.slots.map((s) => <SlotTile key={s.id} s={s} selected={selected} setSelected={setSelected} />)}
                    </div>
                    {activeSelected && !confirmation && (
                      <CapitalBookingForm series={series} slot={selected} member={member} user={user} onCancel={() => setSelected(null)} onDone={(state) => { setConfirmation(state); reload(); }} />
                    )}
                    {activeSelected && confirmation && (
                      <div className="mt-5 pt-4" data-testid="capital-confirmation">
                        <div className="glass-panel p-5 text-center">
                          <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center" style={{ background: "hsl(var(--vd-peach) / 0.14)", color: "hsl(var(--vd-peach-ink))" }}><Sparkles size={22} /></div>
                          <div className="mt-4 font-heading text-xl">Request sent</div>
                          <div className="text-sm text-muted-foreground mt-1">Admin confirms within 24 hours.</div>
                          <button onClick={() => { setConfirmation(null); setSelected(null); }} className="btn-ghost text-sm mt-4">Close</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label }) { return <div className="flex items-center gap-2"><Icon size={12} style={{ color: "hsl(var(--vd-green))" }} /><span>{label}</span></div>; }

export function SlotTile({ s, selected, setSelected }) {
  const isSelected = selected?.id === s.id;
  const isPast = s.is_past || s.status === "past";
  const time = fmtTimePT(s.start_at);
  let cls, label;
  if (isPast) { cls = "slot-past"; label = "Past"; }
  else if (s.status === "booked" || s.status === "pending") { cls = "slot-past"; label = "Booked"; }
  else if (isSelected) { cls = "slot-selected"; label = "Selected"; }
  else { cls = "slot-open"; label = "Open"; }
  const clickable = s.status === "available" && !isPast;
  return (
    <button onClick={() => clickable && setSelected(s)} disabled={!clickable} className={`slot-tile ${cls} ${!clickable ? "cursor-not-allowed" : ""}`} data-testid={`slot-${s.id}`}>
      <div className="font-mono font-medium">{time}</div>
      <div className="text-[10px] uppercase tracking-widest">{label}</div>
    </button>
  );
}

function CapitalBookingForm({ series, slot, member, user, onCancel, onDone }) {
  const [verified, setVerified] = useState(null);
  if (needsVerification(series) && !verified)
    return (
      <div className="mt-5 pt-4"><div className="glass-panel p-5">
        <VerifyGate targetId={series.id} onCancel={onCancel} onVerified={setVerified} />
      </div></div>
    );
  return <SharedCapitalBookingForm series={series} slot={slot} member={member} user={user} verified={verified} onCancel={onCancel} onDone={onDone} />;
}

export function SharedCapitalBookingForm({ series, slot, member, user, verified, onCancel, onDone }) {
  const fields = series.booking_fields || { name: { enabled: true, required: true }, email: { enabled: true, required: true }, linkedin: { enabled: true }, website: { enabled: true }, note: { enabled: true, required: true } };
  const [form, setForm] = useState({ name: verified?.prefill?.name || (member ? `${member.first_name} ${member.last_name}`.trim() : ""), email: verified?.email || member?.email || user?.email || "", linkedin: verified?.prefill?.linkedin || "", website: verified?.prefill?.website || "", note: verified?.prefill?.note || "" });
  const [busy, setBusy] = useState(false);

  // Membership is confirmed server-side after email verification (see VerifyGate
  // -> /register/verify). The old public /member-directory/check debounce is gone.
  const submit = async () => {
    for (const key of ["name", "email", "linkedin", "website", "note"]) {
      const cfg = fields[key]; if (cfg?.enabled && cfg?.required && !String(form[key] || "").trim()) return toast.error(`${key} is required`);
    }
    setBusy(true);
    try {
      await api.post(`/sessions/slots/${slot.id}/book`, { pitch_note: form.note, linkedin: form.linkedin, website: form.website, name: form.name, email: verified?.email || form.email, registration_token: verified?.token });
      toast.success(series.booking_mode === "approval" ? "Request sent — admin confirms within 24h" : "Slot booked");
      onDone({ status: series.booking_mode === "approval" ? "pending" : "booked" });
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  };
  const blocked = false;
  return (
    <div className="mt-5 pt-4" data-testid="booking-panel">
      <div className="glass-panel p-5">
        <div className="flex items-center justify-between mb-4">
          <div><div className="overline">Book your slot</div><div className="font-heading mt-0.5">{fmtTimePT(slot.start_at)} — {fmtTimePT(slot.end_at)} PT</div></div>
          <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground" data-testid="booking-cancel">Cancel</button>
        </div>
        <div className="space-y-3">
          {fields.name?.enabled && <Field label="Full name" required={fields.name.required}><input className="input-field" placeholder="Your full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="capital-name" /></Field>}
          {fields.email?.enabled && (
            <Field label="Email" required={fields.email.required}>
              <input type="email" className="input-field" placeholder="you@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} readOnly={!!verified} data-testid="capital-email" />
            </Field>
          )}
          {fields.linkedin?.enabled && <Field label="LinkedIn" required={fields.linkedin.required}><input className="input-field" placeholder="linkedin.com/in/…" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} data-testid="capital-linkedin" /></Field>}
          {fields.website?.enabled && <Field label="Startup website" required={fields.website.required}><input className="input-field" placeholder="https://" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="capital-website" /></Field>}
          {fields.note?.enabled && <Field label="What are you working on?" required={fields.note.required}><textarea rows={3} maxLength={280} className="input-field resize-none" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="capital-note" /><div className="text-[10px] font-mono text-muted-foreground mt-1 text-right">{form.note.length} / 280</div></Field>}
        </div>
        <div className="mt-4 rounded-xl px-3 py-2.5 text-xs" style={{ background: blocked ? "hsl(var(--vd-coral) / 0.14)" : "hsl(var(--cream))", color: blocked ? "hsl(var(--destructive))" : "hsl(var(--vd-peach-ink))" }} data-testid="capital-member-note">
          {blocked ? (
            <div>
              <div className="font-medium">This email isn&apos;t on the member list.</div>
              <div className="mt-1.5" style={{ color: "hsl(var(--foreground) / 0.75)" }}>
                Not a member yet? <a href="https://venturedock.com/apply/" target="_blank" rel="noreferrer" className="font-medium underline" style={{ color: "hsl(var(--vd-green))" }} data-testid="capital-join-cta">Join Venture Dock</a> to unlock 1:1 sessions like this one, plus investor intros, founder programming, and the rest of the community.
              </div>
            </div>
          ) : MEMBER_NOTE}
        </div>
        <button onClick={submit} disabled={busy || blocked} className="btn-primary w-full mt-4" data-testid="capital-submit">{busy ? "Submitting…" : blocked ? "Members only" : series.booking_mode === "approval" ? "Request slot" : "Book slot"} <ArrowRight size={14} /></button>
      </div>
    </div>
  );
}

function Field({ label, required, children }) { return <label className="block"><div className="overline mb-1">{label}{required && <span style={{ color: "hsl(var(--vd-coral))" }}> *</span>}</div>{children}</label>; }
